/**
 * Storefront API client.
 *
 * Every call sends credentials: the cart lives server-side behind an HTTP-only
 * cookie so a guest's basket survives a refresh, a second tab, and the trip out
 * to the payment page and back.
 */

import type { Attribution } from "./attribution";

const BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(BASE + path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  const text = await res.text();
  const body = text ? safeParse(text) : undefined;

  if (!res.ok) {
    // The API returns { message } on the errors a customer should read, and a
    // bare string on the ones they shouldn't. Prefer the former and fall back
    // to something neutral rather than surfacing a Go error string.
    const message = extractMessage(body) ?? "Ada yang tidak beres. Coba lagi sebentar ya.";
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Digs the human-readable line out of an error body.
 *
 * Echo wraps a handler's payload under `message`, so a handler that answers
 * with a map — short stock, a stale shipping quote — arrives as
 * `{ message: { message: "...", … } }` and needs unwrapping twice. The previous
 * two-step lookup could not do it: its inner step only accepted a string, so
 * every one of those errors fell through to the neutral fallback and the
 * customer was never told what was actually wrong.
 */
function extractMessage(v: unknown): string | undefined {
  if (typeof v === "string") return v;
  if (v && typeof v === "object" && "message" in v) {
    return extractMessage((v as Record<string, unknown>).message);
  }
  return undefined;
}

/**
 * The order was placed but the chosen payment method is unavailable.
 *
 * Returns the way forward — the order exists and holds its stock, so the buyer
 * is sent to its payment page to pay by another method rather than checking out
 * again and ordering twice.
 */
export function qrisUnavailable(err: unknown): { orderNumber: string; statusToken: string } | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const inner = (err.body as { message?: unknown })?.message;
  if (!inner || typeof inner !== "object") return null;
  const m = inner as Record<string, unknown>;
  if (!m.pickVa || typeof m.orderNumber !== "string" || typeof m.statusToken !== "string") {
    return null;
  }
  return { orderNumber: m.orderNumber, statusToken: m.statusToken };
}

/** True when checkout refused because the discount code stopped applying. */
export function discountRejected(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 422) return false;
  const inner = (err.body as { message?: unknown })?.message;
  return Boolean(inner && typeof inner === "object" && (inner as Record<string, unknown>).discount);
}

/** True when checkout refused packing kayu because it was switched off. */
export function packingKayuRejected(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const inner = (err.body as { message?: unknown })?.message;
  return Boolean(inner && typeof inner === "object" && (inner as Record<string, unknown>).packingKayu);
}

/** True when the API is telling us the shipping quote no longer holds. */
export function needsRequote(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body;
  if (!body || typeof body !== "object" || !("message" in body)) return false;
  const inner = (body as Record<string, unknown>).message;
  return Boolean(inner && typeof inner === "object" && (inner as Record<string, unknown>).requote);
}

// ─── catalogue ──────────────────────────────────────────────────────────

export interface ApiSku {
 isPackage?: boolean;
 compareAtIdr?: number;
 priceInvalid?: boolean;
 packageItems?: {skuId:string;qty:number;sku?:ApiSku & {product?:ApiProduct}}[];

 options: Record<string,string>;
  id: string;
  code: string;
  label: string;
  size?: string;
  /** The tabletop finish. */
  topColor?: string;
  /** The frame finish. Desks only — accessories have no leg axis. */
  legColor?: string;
  priceIdr: number;
  isActive: boolean;
  soldOut?: boolean;
}

/**
 * One product photograph, scoped to a colourway.
 *
 * Both colours absent means the product's own gallery: what the catalogue grid
 * shows, and what the product page falls back to for a colourway nobody has
 * photographed yet. A top finish alone is the middle rung, shared by every leg
 * colour until somebody photographs the pairing.
 *
 * Scoped by colourway rather than by variant because size does not change what
 * a desk looks like.
 */
export interface ApiProductImage {
  id: string;
  topColor?: string;
  legColor?: string;
  publicUrl: string;
  alt?: string;
  isPrimary: boolean;
  /** Shown after every colour's set; never the thumbnail. */
  isAdditional?: boolean;
  /** "video" only ever on an additional photo; absent on older responses. */
  mediaType?: "image" | "video";
  sortOrder: number;
}

export interface ApiOptionValue {id:string;label:string;swatchUrl?:string;inquiryOnly?:boolean}
export interface ApiAxis {optionTypeId:string;notApplicable:boolean;valueIds:string[];optionType:{label:string;role:string;control:string;values:ApiOptionValue[]}}
export interface ApiProduct {
  /** Preloaded with the accessory product, so the card can show its photograph. */
  addons?: { skuId: string; sku?: ApiSku & { product?: ApiProduct } }[];
 axes: ApiAxis[];
  id: string;
  slug: string;
  name: string;
  tagline?: string;
  description?: string;
  line: "desk" | "accessory" | "package";
  pasangEligible: boolean;
  soldOut?: boolean;
  skus?: ApiSku[];
  specs?: { label: string; value: string; sortOrder: number }[];
  /** Every colour scope at once, product-level rows first. */
  images?: ApiProductImage[];
}

/** The storefront's email signup popup, as authored in the admin. */
export interface SignupPopup {
  enabled: boolean;
  title: string;
  description: string;
  imageUrl: string;
  /** Shows the email field; buttonLabel and consentText belong to it. */
  collectEmail: boolean;
  buttonLabel: string;
  consentText: string;
  /** Where the popup's link goes: a storefront path or an http(s) URL. Empty for none. */
  ctaLabel: string;
  ctaHref: string;
  delaySeconds: number;
}

/** One picture or video behind the home page hero's copy. */
export interface HeroSlide {
  kind: "image" | "video";
  url: string;
  /** A still for a video, shown while it loads. Empty for none. */
  posterUrl: string;
  /** Empty is decorative. */
  alt: string;
}

/** The home page hero, as authored in the admin under "Landing page". */
export interface LandingHero {
  /** Empty hides the pill. */
  badge: string;
  /** A newline is a line break. */
  title: string;
  subtitle: string;
  /** A storefront path or an http(s) URL. */
  ctaLabel: string;
  ctaHref: string;
  /** The white button. Empty label hides it. */
  secondaryLabel: string;
  /** "whatsapp" for Kanata's WhatsApp, a storefront path, or an http(s) URL. */
  secondaryHref: string;
  /** Empty: show the built-in photograph. */
  slides: HeroSlide[];
  /** How long an image stays up. A video plays through. */
  intervalSeconds: number;
}

/** One card in the promo banner's row of extras. Mirrors handlers.LandingPromoItem. */
export interface LandingPromoItem {
  /** An uploaded image, or one of the storefront's own under /promo/. */
  image: string;
  /** A newline is a line break, e.g. "FREE\nPacking Kayu". */
  title: string;
  subtitle: string;
}

/**
 * The "Lebih lengkap," banner under the home page hero, as
 * authored in the admin under "Landing page". Mirrors handlers.LandingPromo.
 */
export interface LandingPromo {
  /** Off the home page. Unused on a campaign page, whose section hides itself. */
  hidden: boolean;
  /** The pill above the headline. Empty hides it. */
  label: string;
  /** The headline's white line, and its green one. */
  title: string;
  accent: string;
  body: string;
  items: LandingPromoItem[];
  /** The red sticker's three lines, small, big, small. All empty hides it. */
  stickerLabel: string;
  stickerValue: string;
  stickerNote: string;
  image: string;
  imageAlt: string;
  /** An optional button, both or neither. A storefront path or an http(s) URL. */
  ctaLabel: string;
  ctaHref: string;
}

/**
 * The automatic promotion live right now, as advertised on every page. What a
 * cart is actually given is in Cart.promo; this is the advert.
 */
export interface PublicPromo {
  id: string;
  name: string;
  tagline?: string;
  /** Exclusive: "until 31 December" is 1 January 00:00 WIB. */
  endsAt?: string;
  shippingSubsidyMaxIdr: number;
  gift?: { productName: string; productSlug: string; priceIdr: number };
  /** Present while the promotion waives packing kayu: its price per desk. */
  packingKayuPriceIdr?: number;
}

export const promoApi = {
  current: () => request<{ promo: PublicPromo | null }>("/promo/current"),
};

/** The Google tag IDs, as set in the admin under Tracking. Empty for none. */
export interface TrackingConfig {
  ga4MeasurementId: string;
  googleAdsId: string;
  googleAdsPurchaseLabel: string;
}

export const trackingApi = {
  config: () => request<TrackingConfig>("/tracking/config"),
};

/** One block of a campaign page. Mirrors handlers.CampaignSection. */
export type CampaignSection =
  | { type: "hero"; hero: LandingHero }
  | { type: "products"; products: { title: string; slugs: string[]; line: "" | "desk" | "accessory" } }
  | { type: "offer"; offer: CampaignOffer }
  | { type: "promo"; promo: LandingPromo }
  /** "range" is the home page's products section: every desk and accessory, side by side. */
  | { type: "reviews" | "setup" | "offices" | "range" };

export interface CampaignOffer {
  title: string;
  body: string;
  /** A discount code to copy. Empty for none. */
  code: string;
  /** The last day, YYYY-MM-DD in WIB. Empty for no end. */
  endsOn: string;
}

/** A campaign page at /lp/:slug, as authored in the admin. Hidden sections never arrive. */
export interface CampaignPage {
  slug: string;
  title: string;
  description: string;
  status: "draft" | "published";
  sections: CampaignSection[];
}

export const campaignApi = {
  get: (slug: string, preview?: string) =>
    request<CampaignPage>(
      `/campaign-pages/${encodeURIComponent(slug)}` + (preview ? `?preview=${encodeURIComponent(preview)}` : ""),
    ),
};

export const landingApi = {
  hero: () => request<LandingHero>("/landing/hero"),
  promo: () => request<LandingPromo>("/landing/promo"),
};

export const marketingApi = {
  signupPopup: () => request<SignupPopup>("/marketing/signup-popup"),
  subscribe: (email: string) =>
    request<{ subscribed: boolean }>("/marketing/subscribe", { method: "POST", body: JSON.stringify({ email }) }),
};

export const catalog = {
  list: () => request<{ products: ApiProduct[] }>("/catalog/products"),
  detail: (slug: string) => request<ApiProduct>(`/catalog/products/${slug}`),
  featured: () => request<{ products: ApiProduct[] }>("/catalog/featured"),
};

// ─── cart ───────────────────────────────────────────────────────────────

export interface CartLine {
  skuId: string;
  skuCode: string;
  label: string;
  productName: string;
  productSlug: string;
  size?: string;
  topColor?: string;
  legColor?: string;
  /** What the cart stores. Controls act on this number. */
  qty: number;
  /** How much of `qty` went into an upgrade. `qty - upgradedQty` is what is
   *  still this line; `lineTotalIdr` prices only that remainder. */
  upgradedQty: number;
  unitPriceIdr: number;
  lineTotalIdr: number;
  /** One unit's harga coret. Absent when the variant has none. */
  listPriceIdr?: number;
  /** What the harga coret saves on the remainder `lineTotalIdr` prices. */
  listSavingIdr?: number;
  pasang: boolean;
  pasangEligible: boolean;
  imageUrl?: string;
}

/** One cart line's share of a single upgraded unit. */
export interface CartUpgradeSource {
  skuId: string;
  label: string;
  productName: string;
  qty: number;
}

/**
 * Separately-chosen lines the cart is charging as a package-priced product:
 * Klasik + a Raya stand in the same colour, as Plus. Computed by the server on
 * every read, never stored — removing the stand line undoes it.
 */
export interface CartUpgrade {
  skuId: string;
  skuCode: string;
  productName: string;
  productSlug: string;
  label: string;
  size?: string;
  topColor?: string;
  legColor?: string;
  qty: number;
  unitPriceIdr: number;
  lineTotalIdr: number;
  /** One unit bought as the separate lines. */
  compareAtIdr: number;
  /** For all `qty` units. */
  savingIdr: number;
  pasang: boolean;
  pasangEligible: boolean;
  imageUrl?: string;
  sources: CartUpgradeSource[];
  /** The Bahasa sentence explaining the change, to show as-is. */
  notice: string;
}

export interface Cart {
  cartId: string;
  items: CartLine[];
  upgrades: CartUpgrade[];
  /** After upgrades. What checkout charges before shipping. */
  subtotalIdr: number;
  /** The same cart with every line priced on its own. */
  compareAtSubtotalIdr: number;
  /** What the lines' harga coret saves, on top of any package saving. */
  listSavingIdr?: number;
  pasangIncluded: boolean;
  /** Desks in the cart, a package's desks included. */
  deskUnits: number;
  /** What crating every desk costs. Absent with no desk, or when switched off. */
  packingKayu?: PackingKayuOffer;
  /**
   * What the live automatic promotion gives this cart. Absent when none is
   * live or there is no desk. Checkout decides again; this is the preview.
   */
  promo?: CartPromo;
}

/** The automatic promotion (the Paket Pembukaan) as it applies to a cart. */
export interface CartPromo {
  id: string;
  name: string;
  tagline?: string;
  endsAt?: string;
  deskUnits: number;
  /** Per order, off the courier picked: min(quote, this). */
  shippingSubsidyMaxIdr: number;
  /** The gift and the crate, before the shipping subsidy. */
  savingIdr: number;
  /** Present when the promotion waives packing kayu: every desk crated free. */
  packingKayu?: PackingKayuOffer;
  gift?: CartPromoGift;
}

/**
 * The free line. Not stored in the cart — it follows the desks — so it has
 * its own controls: take it out, put it back.
 */
export interface CartPromoGift {
  skuId: string;
  label: string;
  productName?: string;
  productSlug?: string;
  imageUrl?: string;
  unitPriceIdr: number;
  /** One per desk. */
  wanted: number;
  /** What is given: wanted, unless declined or the shelf is short. */
  qty: number;
  declined: boolean;
  short: boolean;
}

/** The packing kayu add-on as offered for this cart: one crate per desk. */
export interface PackingKayuOffer {
  units: number;
  priceIdr: number;
  totalIdr: number;
}

export const cartApi = {
  get: () => request<Cart>("/cart"),
  add: (skuId: string, qty = 1, pasang?: boolean, addonIds: string[] = []) =>
    request<Cart>("/cart/items", { method: "POST", body: JSON.stringify({ skuId, qty, pasang, addonIds }) }),
  setQty: (skuId: string, qty: number) =>
    request<Cart>(`/cart/items/${skuId}`, { method: "PATCH", body: JSON.stringify({ qty }) }),
  setPasang: (skuId: string, pasang: boolean) =>
    request<Cart>(`/cart/items/${skuId}`, { method: "PATCH", body: JSON.stringify({ pasang }) }),
  remove: (skuId: string) => request<Cart>(`/cart/items/${skuId}`, { method: "DELETE" }),
  /** Take the promotion's free gift out of the cart, or put it back. */
  setPromoGift: (include: boolean) =>
    request<Cart>("/cart/promo-gift", { method: "PUT", body: JSON.stringify({ include }) }),
  /**
   * Would this product-page selection be charged as a package in the cart?
   * Stateless — reads no cart, writes nothing — so the page asks on every tick.
   */
  upgradePreview: (skuId: string, qty: number, addonIds: string[]) =>
    request<{ upgrades: UpgradePreview[] }>("/cart/upgrade-preview", {
      method: "POST",
      body: JSON.stringify({ skuId, qty, addonIds }),
    }),
};

/** A selection the cart will charge as a package, said before it is added. */
export interface UpgradePreview {
  skuId: string;
  productName: string;
  qty: number;
  unitPriceIdr: number;
  compareAtIdr: number;
  savingIdr: number;
  /** Bahasa, ready to show. */
  notice: string;
}

// ─── checkout ───────────────────────────────────────────────────────────

export interface ShippingRate {
  /**
   * The server's record of this offer. Checkout sends this back and nothing
   * else about the courier — the price and the service are read from the
   * stored quote, so neither can be edited on the way through.
   *
   * It is also the only stable key in the list: service codes like "reg" and
   * "std" recur across couriers, so keying the radio group on one silently
   * selected the first of two matching rates.
   */
  quote_id: string;
  courier_code: string;
  courier_name: string;
  courier_service_code: string;
  courier_service_name: string;
  description: string;
  duration: string;
  price: number;
}

export interface CheckoutPayload {
  cartId: string;
  name: string;
  email: string;
  phone: string;
  recipientName: string;
  addressLine: string;
  district?: string;
  village?: string;
  city: string;
  province: string;
  postalCode: string;
  shippingQuoteId: string;
  pasangIncluded: boolean;
  pasangNote?: string;
  /** Crate every desk. The price is read at checkout, never sent. */
  packingKayu?: boolean;
  customerNote?: string;
  paymentMethod: "QRIS" | "VA";
  /** Optional. The server evaluates it; the amount is never sent. */
  discountCode?: string;
  /** The bank for a branded VA, when the server offers banks. */
  vaBank?: string;
  /** Where this visit came from; see lib/attribution.ts. */
  attribution?: Attribution;
  /** The Pixel's cookies, for the server's copy of the purchase. */
  meta?: { fbp?: string; fbc?: string };
  /** GA's client and session ids, for the server's copy of the purchase. */
  ga?: { clientId?: string; sessionId?: string };
  /**
   * Mixpanel's device and environment, for the server's order_paid (DEL-444).
   * Absent where Mixpanel is off; no deviceId where the SDK was blocked.
   */
  mixpanel?: { deviceId?: string; environment: string };
}

/** A discount code the server accepted for the current cart. */
export interface DiscountPreview {
  code: string;
  discountIdr: number;
  /** The cart's subtotal after any upgrade, which the code comes off. */
  subtotalIdr: number;
}

/**
 * What the buyer needs in order to pay.
 *
 * Two kinds, because there are two things a buyer can be given: something to
 * scan, or somewhere to go. Note what is NOT here — no QR image URL. The page
 * renders the code itself from `qrPayload`, so nothing about it depends on a
 * payment processor's CDN staying up, staying fast, or keeping its URLs.
 */
export interface ApiCharge {
  /** "va": a branded virtual account (DEL-439), shown on Kanata's own page. */
  kind: "qris" | "redirect" | "va";
  /** The bank and the number to transfer to, on the "va" path. */
  bankCode?: string;
  bankName?: string;
  accountNumber?: string;
  /** Raw EMVCo QRIS payload, on the QRIS path. Rendered client-side. */
  qrPayload?: string;
  /** The hosted page, on the VA path. */
  redirectUrl?: string;
  /** The payment processor's reference, worth quoting if somebody calls. */
  referenceNo?: string;
  /** RFC 3339. Absent when the processor did not say. */
  expiresAt?: string;
  /**
   * True when the API is serving a charge from its in-memory fake, because no
   * Altura key is configured.
   *
   * The page says so out loud. A fake charge looks exactly like a real one —
   * it renders a QR a camera reads perfectly well, and the banking app then
   * rejects it on the checksum — so without this the time goes into the
   * renderer, the screen brightness and the phone, none of which is ever the
   * cause.
   */
  fake?: boolean;
}

/** The four states the payment page renders. */
export type PaymentState = "pending" | "paid" | "failed" | "expired";

export interface ApiPaymentPage {
  orderNumber: string;
  totalIdr: number;
  paymentMethod?: string;
  state: PaymentState;
  charge?: ApiCharge;
  /**
   * Whether to render a live countdown against `charge.expiresAt`.
   *
   * Served by the API rather than built into this bundle because the storefront
   * is prerendered: a build-time flag could only be changed by rebuilding and
   * redeploying the whole site. This one is an env change on the API.
   */
  countdown: boolean;
  /** Present when re-minted — on recharge, and on the guest lookup path. */
  statusToken?: string;
}

/** The poll payload: state and expiry, and deliberately nothing else. */
export interface ApiPaymentStatus {
  state: PaymentState;
  expiresAt?: string;
}

/** A bank the buyer can pick for a branded VA. */
export interface VaBank {
  code: string;
  name: string;
}

export const checkoutApi = {
  /** Banks for a branded VA. Empty: VA goes to the payment partner's page. */
  paymentOptions: () => request<{ vaBanks: VaBank[] }>("/checkout/payment-options"),
  quote: (cartId: string, postalCode: string) =>
    request<{ rates: ShippingRate[] }>("/checkout/shipping-quote", {
      method: "POST",
      body: JSON.stringify({ cartId, postalCode }),
    }),
  previewDiscount: (cartId: string, code: string) =>
    request<DiscountPreview>("/checkout/discount-preview", {
      method: "POST",
      body: JSON.stringify({ cartId, code }),
    }),
  submit: (payload: CheckoutPayload) =>
    request<{
      orderNumber: string;
      totalIdr: number;
      charge: ApiCharge;
      /** The buyer's key to their own payment page. */
      statusToken: string;
    }>("/checkout", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  lookup: (orderNumber: string, phone: string) =>
    request<ApiOrder>(
      `/orders/lookup?orderNumber=${encodeURIComponent(orderNumber)}&phone=${encodeURIComponent(phone)}`,
    ),

  // ── the branded payment page ──────────────────────────────────────────
  //
  // All three are guarded by the token minted at checkout rather than by a
  // session: guest checkout is the default path, and asking somebody
  // mid-payment to re-type their phone number is the wrong moment for a second
  // identity check.

  /** Everything the page needs to render itself. No processor call server-side. */
  payment: (orderNumber: string, token: string) =>
    request<ApiPaymentPage>(
      `/checkout/${encodeURIComponent(orderNumber)}/payment?t=${encodeURIComponent(token)}`,
    ),
  /** The live "has it been paid yet". Polled; see lib/payment-poll.ts. */
  paymentStatus: (orderNumber: string, token: string) =>
    request<ApiPaymentStatus>(
      `/checkout/${encodeURIComponent(orderNumber)}/status?t=${encodeURIComponent(token)}`,
    ),
  /**
   * "Start over": a fresh charge against the same order, never a new order.
   *
   * `payWith` switches the payment method on the way through. That is the route
   * out of the one corner the rollback switch can create — QRIS unavailable on
   * an order that already exists and holds its stock — without placing a second
   * order for a second desk.
   */
  recharge: (orderNumber: string, token: string, payWith?: "QRIS" | "VA") =>
    request<ApiPaymentPage>(
      `/checkout/${encodeURIComponent(orderNumber)}/recharge?t=${encodeURIComponent(token)}` +
        (payWith ? `&method=${payWith}` : ""),
      { method: "POST" },
    ),
};

export interface ApiOrder {
  orderNumber: string;
  status: string;
  subtotalIdr: number;
  /** Taken off by a discount code; shipping is never discounted. */
  discountIdr?: number;
  discountCode?: string;
  /** Desks crated, and what the crates cost. Zero when not asked for. */
  packingKayuUnits?: number;
  packingKayuIdr?: number;
  /** The automatic promotion, as the order was given it. */
  promoName?: string;
  promoGiftIdr?: number;
  promoPackingIdr?: number;
  shippingSubsidyIdr?: number;
  shippingFeeIdr: number;
  totalIdr: number;
  pasangIncluded: boolean;
  paymentMethod?: string;
  alturaPaymentLink?: string;
  /**
   * Present when the order can still be paid. It is the way back to the
   * payment page for a buyer who closed the tab — safe to mint here because
   * lookup already demanded the order number *and* the phone, which is a
   * stronger check than the token itself represents.
   */
  statusToken?: string;
  biteshipCourierName?: string;
  biteshipWaybillId?: string;
  /** Biteship's public tracking page, once a courier is booked. */
  biteshipTrackingUrl?: string;
  paidAt?: string;
  createdAt: string;
  items?: {
    productName: string;
    skuLabel: string;
    size?: string;
    topColor?: string;
    legColor?: string;
    qty: number;
    unitPriceIdr: number;
    lineTotalIdr: number;
    pasang: boolean;
    /** Given free by the promotion; lineTotalIdr is its value, not a charge. */
    promoGift?: boolean;
  }[];
}
